import React, { useState, useRef } from "react";
import axios from "axios";
import DOMPurify from "dompurify";
import { Popover, Overlay } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import "./TooltipComponent.scss";
import { faInfoCircle, faTimes } from "@fortawesome/free-solid-svg-icons";

export interface TooltipInterface {
  title: string;
  url?: string;
  staticText?: string;
  screenreaderText?: string;
}

export const TooltipComponent: React.FC<TooltipInterface> = (props) => {
  const [showPopover, setShowPopover] = useState(false);
  const [content, setContent] = useState("");
  const target = useRef<HTMLButtonElement>(document.createElement("button"));

  const getContent = async () => {
    if (props.url) {
      try {
        const response = await axios.get(props.url);
        const html = response.data;
        const cleanHtml = DOMPurify.sanitize(html, {});
        setContent(cleanHtml);
      } catch (error) {
        console.log(`Content Request failed: ${error}`);
      }
    } else {
      const staticText = props.staticText ? props.staticText : "";
      setContent(staticText);
    }
    target.current?.setAttribute("aria-expanded", "true");
    setTimeout(() => {
      target.current?.setAttribute("aria-describedby", "popover");
      document.getElementById("popover")?.focus();
    }, 300);
    setShowPopover(true);
  };

  const closePopover = () => {
    target.current?.setAttribute("aria-expanded", "false");
    target.current?.removeAttribute("aria-describedby");
    target.current?.focus();
    setShowPopover(false);
  };

  const handleClick = () => {
    showPopover ? closePopover() : getContent();
  };

  return (
    <>
      <button
        type="button"
        ref={target}
        className="aru-tooltip-link"
        aria-expanded="false"
        onClick={(e) => {
          e.preventDefault();
          handleClick();
        }}
      >
        <span className="sr-only">{props.screenreaderText}</span>
        <FontAwesomeIcon icon={faInfoCircle} />
      </button>
      <Overlay
        target={target.current}
        placement="bottom"
        show={showPopover}
        onHide={() => {
          closePopover();
        }}
        rootClose
        rootCloseEvent="click"
      >
        <Popover
          className="aru-popover"
          style={{
            maxWidth: content.length >= 300 ? 500 : 276,
            outline: 0,
          }}
          id="popover"
          tabIndex={-1}
        >
          <Popover.Title id="poptitle">
            <strong>{props.title}</strong>
            <button
              type="button"
              className="aru-popover-close"
              onClick={() => {
                closePopover();
              }}
            >
              <FontAwesomeIcon icon={faTimes} />
              <span className="sr-only">Close</span>
            </button>
          </Popover.Title>
          <Popover.Content
            id="popcontent"
            dangerouslySetInnerHTML={{ __html: content }}
          ></Popover.Content>
        </Popover>
      </Overlay>
    </>
  );
};
